import React, { useState } from "react";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

/**
 * Import products from a source. Currently the only source is Etsy (search
 * URL based). Adding a new source only requires:
 *   1. New adapter on the server (services/productSources/*).
 *   2. Adding an <option> below and, if needed, a source-specific input.
 *
 * Note: source credentials (Etsy keystring) are configured server-side via
 * .env, not through this UI.
 */
const ImportFromSourceForm = ({
  workspaceId,
  categories,
  setPopupShow,
  onImported,
}) => {
  const DEFAULT_PAGE_SIZE = 50;
  const [source, setSource] = useState("etsy");
  const [searchUrl, setSearchUrl] = useState("");
  const [maxPages, setMaxPages] = useState(1);

  const [categoryMode, setCategoryMode] = useState("existing"); // 'existing' | 'new'
  const [category, setCategory] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");

  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchUrl.trim()) {
      toast.error("Vui lòng nhập URL tìm kiếm");
      return;
    }
    if (categoryMode === "existing" && !category) {
      toast.error("Vui lòng chọn category");
      return;
    }
    if (categoryMode === "new" && !newCategoryName.trim()) {
      toast.error("Vui lòng nhập tên category mới");
      return;
    }

    try {
      setImporting(true);
      setResult(null);
      const payload = {
        workspace: workspaceId,
        source,
        searchUrl: searchUrl.trim(),
        maxPages: Number(maxPages) || 1,
        pageSize: DEFAULT_PAGE_SIZE,
      };
      if (categoryMode === "existing") payload.category = category;
      else payload.categoryName = newCategoryName.trim();

      const res = await Utils._apiRequest({
        path: Enums.endpoints.products.IMPORT,
        type: "POST",
        payload,
      });
      if (res.errors) throw new Error(res.errors);
      setResult(res);
      toast.success(res.message || "Đã import");
      onImported && onImported();
    } catch (err) {
      toast.error(err.message || "Lỗi khi import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="products-import-form">
      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Nguồn *</Form.Label>
            <Form.Select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="etsy">Etsy</option>
            </Form.Select>
            <div className="form-hint mt-1">Hỗ trợ thêm nguồn khác trong tương lai.</div>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Max pages *</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={20}
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Page size</Form.Label>
            <div className="form-hint mt-2">Page size mặc định: {DEFAULT_PAGE_SIZE}</div>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group>
            <Form.Label>URL tìm kiếm *</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://www.etsy.com/search?q=leather+wallet&min=10&max=50"
              value={searchUrl}
              onChange={(e) => setSearchUrl(e.target.value)}
            />
            <div className="form-hint mt-1">
              Copy URL từ trang tìm kiếm của Etsy (hỗ trợ query, min/max price, sort).
            </div>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Label>Lưu vào category *</Form.Label>
          <div className="d-flex gap-3 mb-2">
            <Form.Check
              type="radio"
              id="cat-existing"
              label="Chọn category hiện có"
              checked={categoryMode === "existing"}
              onChange={() => setCategoryMode("existing")}
            />
            <Form.Check
              type="radio"
              id="cat-new"
              label="Tạo category mới"
              checked={categoryMode === "new"}
              onChange={() => setCategoryMode("new")}
            />
          </div>
          {categoryMode === "existing" ? (
            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">-- Chọn category --</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control
              placeholder="Tên category mới"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          )}
        </Col>
      </Row>

      {result && (
        <Alert variant="success" className="mt-3 mb-0">
          <div><strong>Kết quả:</strong> {result.message}</div>
          <small>
            Fetched: {result.totalFetched} · Inserted: {result.inserted} ·
            Updated: {result.updated} · Failed: {result.failed}
          </small>
        </Alert>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="secondary" onClick={() => setPopupShow(false)} disabled={importing}>
          Đóng
        </Button>
        <Button variant="primary" type="submit" disabled={importing}>
          {importing ? "Đang import..." : "Bắt đầu import"}
        </Button>
      </div>
    </Form>
  );
};

export default ImportFromSourceForm;
